<template>
  <div v-if="isLoadingAuth || isLoading" class="fixed inset-0 z-50 flex items-center justify-center bg-gray-900 bg-opacity-70">
    <div class="animate-spin rounded-full h-12 w-12 border-t-4 border-indigo-600"></div>
  </div>
  <!-- Not logged in -->
  <div v-else-if="!user" class="fixed inset-0 z-50 flex items-center justify-center bg-gray-900 bg-opacity-70">
    <div class="flex items-center gap-3 bg-gray-800 border border-gray-700 px-4 py-3 rounded-md">
      <i class="fas fa-exclamation-triangle text-2xl text-red-400"></i>
      <span class="text-lg font-semibold text-indigo-200">กรุณาเข้าสู่ระบบ</span>
    </div>
  </div>
  <!-- Main Modal -->
  <div v-else class="fixed inset-0 bg-gray-900 bg-opacity-70 backdrop-blur-sm flex items-center justify-center p-4 z-50 animate-modalFade">
    <div
      class="w-full max-w-md bg-gray-800 text-gray-300 rounded-lg p-4 sm:p-6 flex flex-col gap-4 shadow-lg border border-gray-700 animate-scaleIn relative"
      role="dialog"
      aria-modal="true"
      aria-labelledby="stat-modal-title"
      @keydown.esc="cancelModal"
    >
      <!-- Tabs -->
      <div class="flex justify-center border-b border-gray-700">
        <button
          @click="switchTab('upgrade')"
          class="px-4 py-2 text-sm font-semibold transition-colors duration-200"
          :class="activeTab === 'upgrade' ? 'text-indigo-300 border-b-2 border-indigo-600' : 'text-gray-400 hover:text-indigo-300'"
          aria-controls="tab-upgrade"
          :aria-selected="activeTab==='upgrade'"
        >
          อัพเกรดสเตตัส
        </button>
        <button
          @click="switchTab('reset')"
          class="px-4 py-2 text-sm font-semibold transition-colors duration-200"
          :class="activeTab === 'reset' ? 'text-indigo-300 border-b-2 border-indigo-600' : 'text-gray-400 hover:text-indigo-300'"
          aria-controls="tab-reset"
          :aria-selected="activeTab==='reset'"
        >
          รีสเตตัส
        </button>
      </div>
      <!-- Close -->
      <button
        @click="cancelModal"
        class="absolute top-3 right-3 text-gray-400 hover:text-indigo-300 transition-colors duration-200"
        :disabled="isProcessing"
        aria-label="ปิด"
      >
        <i class="fas fa-times text-lg"></i>
      </button>
      <!-- Upgrade Tab -->
      <div v-if="activeTab === 'upgrade'" id="tab-upgrade" class="space-y-4">
        <!-- Points summary -->
        <div class="bg-gray-800 rounded-md p-3 border border-gray-700">
          <div class="flex items-center justify-between text-sm">
            <div class="text-indigo-200">
              แต้มที่มี: <span class="font-semibold text-white">{{ remainingPoints }}</span>
            </div>
            <div class="text-indigo-200">
              ใช้แล้ว: <span class="font-semibold text-indigo-300">{{ usedPoints }}</span> / {{ statPoints }}
            </div>
          </div>
          <!-- Progress (used points) -->
          <div class="mt-2 h-2 bg-gray-700 rounded overflow-hidden border border-gray-700">
            <div
              class="h-full bg-indigo-600"
              :style="{ width: progressWidth }"
            ></div>
          </div>
        </div>
        <!-- Upgrade Recommendation -->
        <div class="text-center text-sm text-yellow-300">
          คำแนะนำ: ควรอัปเกรด str 99 เพื่อเพิ่มพลังในการโจมตี
        </div>
        <!-- Stats list -->
        <div class="space-y-2 max-h-72 overflow-y-auto" ref="statList" id="style-1">
          <div
            v-for="stat in statOrder"
            :key="stat"
            class="flex items-center justify-between rounded-md p-3 text-sm border border-gray-700 bg-gray-800 transition-all duration-200 hover:bg-gray-700 hover:border-indigo-500"
          >
            <span class="font-medium text-gray-100 capitalize">{{ labelMap[stat] }}</span>
            <div class="flex items-center gap-1">
              <button
                @click="decreaseStat(stat)"
                :disabled="(tempStats[stat] || 0) <= (stats[stat] || 0) || isProcessing"
                class="px-2 py-1 bg-red-600 hover:bg-red-700 text-white rounded-md text-xs border border-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                aria-label="ลดค่า"
              >-</button>
              <input
                type="number"
                v-model.number="tempStats[stat]"
                class="w-14 text-center bg-gray-800 text-white rounded text-sm border border-gray-600"
                :min="stats[stat] || 0"
                step="1"
                spellcheck="false"
                @input="clampStat(stat)"
                @keyup.enter="confirmUpgrade"
                aria-label="ค่าค่าสถานะ"
              />
              <button
                @click="increaseStat(stat)"
                :disabled="remainingPoints <= 0 || (tempStats[stat] || 0) >= 99 || isProcessing"
                class="px-2 py-1 bg-indigo-600 hover:bg-indigo-700 text-white rounded-md text-xs border border-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                aria-label="เพิ่มค่า"
              >+</button>
            </div>
          </div>
        </div>
        <!-- Actions -->
        <div class="space-y-2">
          <button
            @click="confirmUpgrade"
            :disabled="remainingPoints === statPoints || isProcessing || usedPoints===0"
            class="w-full bg-blue-600 hover:bg-blue-700 text-white py-2 rounded-md text-sm transition-colors duration-200 border border-blue-700 flex justify-center items-center"
          >
            <span v-if="!isProcessing">ยืนยันการอัพสเตตัส</span>
            <div v-else class="animate-spin rounded-full h-4 w-4 border-t-2 border-white"></div>
          </button>
          <button
            @click="cancelModal"
            class="w-full bg-gray-700 hover:bg-gray-600 text-white py-2 rounded-md text-sm border border-gray-600 transition-colors duration-200"
          >ยกเลิก</button>
        </div>
      </div>
      <!-- Reset Tab -->
      <div v-else-if="activeTab === 'reset'" id="tab-reset" class="space-y-4">
        <div class="bg-gray-800 rounded-md p-3 border border-gray-700 text-center text-sm text-indigo-200">
          <div>เลเวล: <span class="font-semibold text-white">{{ level }}</span></div>
          <div>แต้มที่จะได้รับ: <span class="font-semibold text-white">{{ newStatPoints }}</span></div>
        </div>
        <div class="text-center text-sm text-yellow-300">
          การรีสเตตัสจะคืนค่า stats ทั้งหมดเป็น 0 และใช้ 10 cash
        </div>
        <div v-if="!showConfirmReset" class="space-y-2">
          <button
            @click="showConfirmReset = true"
            :disabled="isProcessing || cash < 10"
            class="w-full bg-blue-600 hover:bg-blue-700 text-white py-2 rounded-md text-sm border border-blue-700 flex justify-center items-center disabled:opacity-50"
          >รีสเตตัส</button>
          <button
            @click="cancelModal"
            class="w-full bg-gray-700 hover:bg-gray-600 text-white py-2 rounded-md text-sm border border-gray-600 transition-colors duration-200"
          >ยกเลิก</button>
        </div>
        <div v-else class="space-y-2">
          <div class="text-center text-sm text-red-300">
            คุณแน่ใจหรือไม่? การรีสเตตัสจะคืนค่า stats เป็น 0 และใช้ 10 cash
          </div>
          <button
            @click="confirmReset"
            :disabled="isProcessing || cash < 10"
            class="w-full bg-red-600 hover:bg-red-700 text-white py-2 rounded-md text-sm border border-red-700 flex justify-center items-center disabled:opacity-50"
          >ยืนยันการรีสเตตัส</button>
          <button
            @click="showConfirmReset = false"
            class="w-full bg-gray-700 hover:bg-gray-600 text-white py-2 rounded-md text-sm border border-gray-600 transition-colors duration-200"
          >ยกเลิก</button>
        </div>
      </div>
      <!-- Messages -->
      <div v-if="successMessage" class="mt-1 text-center text-indigo-300 text-sm">
        {{ successMessage }}
      </div>
      <div v-if="errorMessage" class="mt-1 text-center text-red-300 text-sm">
        {{ errorMessage }}
      </div>
    </div>
  </div>
</template>
<script>
import getUser from '@/composables/getUser';
import { projectFunctions } from '@/firebase/config';
import { ref, watch, computed, onUnmounted } from 'vue';
import { usePlayerStore } from '@/composables/usePlayerStore';
export default {
  name: 'StatUpgradeModal',
  setup(props, { emit }) {
    const { user, isLoadingAuth } = getUser();
    const playerStore = usePlayerStore();
    const stats = ref({});
    const tempStats = ref({});
    const statPoints = ref(0);
    const cash = computed(() => playerStore.userData?.cash || 0);
    const level = computed(() => playerStore.characterData?.level || 1);
    const isLoading = ref(true);
    const error = ref(null);
    const isProcessing = ref(false);
    const successMessage = ref('');
    const errorMessage = ref('');
    const activeTab = ref('upgrade');
    const statOrder = ['stamina', 'str', 'agi', 'int'];
    const labelMap = { stamina: 'Stamina', str: 'STR', agi: 'AGI', int: 'INT' };
    const showConfirmReset = ref(false);
    const usedPoints = computed(() => {
      return Object.entries(tempStats.value).reduce((sum, [k, v]) => {
        const base = stats.value[k] || 0;
        return sum + Math.max(0, (v || 0) - base);
      }, 0);
    });
    const remainingPoints = computed(() => Math.max(0, statPoints.value - usedPoints.value));
    const newStatPoints = computed(() => (level.value >= 2 ? (level.value - 1) * 5 : 0));
    const progressWidth = computed(() => {
      if (!statPoints.value) return '0%';
      const pct = Math.min(100, Math.floor((usedPoints.value / statPoints.value) * 100));
      return pct + '%';
    });
    const switchTab = (tab) => {
      activeTab.value = tab;
      showConfirmReset.value = false;
    };
    watch(() => playerStore.characterPrivateData, (data) => {
      if (data) {
        statPoints.value = data.statPoints || 0;
        stats.value = data.stats || { stamina: 0, str: 0, agi: 0, int: 0 };
        tempStats.value = { ...stats.value };
        isLoading.value = false;
      } else {
        isLoading.value = false;
        error.value = 'ไม่พบข้อมูลตัวละครส่วนตัว';
      }
    }, { immediate: true });
    const increaseStat = (stat) => {
      if (remainingPoints.value > 0 && (tempStats.value[stat] || 0) < 99) {
        tempStats.value = { ...tempStats.value, [stat]: (tempStats.value[stat] || 0) + 1 };
      }
    };
    const decreaseStat = (stat) => {
      const original = stats.value[stat] || 0;
      if ((tempStats.value[stat] || 0) > original) {
        tempStats.value = { ...tempStats.value, [stat]: tempStats.value[stat] - 1 };
      }
    };
    const clampStat = (stat) => {
      const original = stats.value[stat] || 0;
      let val = Number(tempStats.value[stat]);
      if (!Number.isFinite(val)) val = original;
      if (val < original) val = original;
      if (!Number.isInteger(val)) val = Math.floor(val);
      let usedWithout = 0;
      statOrder.forEach((s) => {
        if (s !== stat) usedWithout += Math.max(0, (tempStats.value[s] || 0) - (stats.value[s] || 0));
      });
      const maxForThis = original + (statPoints.value - usedWithout);
      val = Math.min(val, Math.min(99, maxForThis));
      tempStats.value[stat] = val;
    };
    const confirmUpgrade = async () => {
      if (isProcessing.value || !user.value || usedPoints.value === 0) return;
      isProcessing.value = true;
      successMessage.value = '';
      errorMessage.value = '';
      try {
        const characterActions = projectFunctions.httpsCallable('gameActions');
        const updatedStats = {};
        statOrder.forEach((s) => {
          const base = stats.value[s] || 0;
          const cur = tempStats.value[s] || 0;
          if (cur > base) {
            if (cur > 99) throw new Error(`ค่าสถานะ ${labelMap[s]} ไม่สามารถเกิน 99 ได้`);
            updatedStats[s] = cur;
          }
        });
        if (Object.keys(updatedStats).length === 0) {
          isProcessing.value = false;
          return;
        }
        const result = await characterActions({
          action: 'characterActions',
          subAction: 'manageStats',
          type: 'upgrade',
          stats: updatedStats,
          usedPoints: usedPoints.value
        });
        if (result.data.success) {
          stats.value = { ...tempStats.value };
          statPoints.value = result.data.newStatPoints != null ? result.data.newStatPoints : (statPoints.value - usedPoints.value);
          successMessage.value = result.data.message || 'อัพเกรดสำเร็จ';
        } else {
          throw new Error(result.data.message || 'การอัพเกรดล้มเหลว');
        }
      } catch (err) {
        errorMessage.value = err.message || 'เกิดข้อผิดพลาดในการอัพเกรด';
      } finally {
        isProcessing.value = false;
      }
    };
    const confirmReset = async () => {
      if (isProcessing.value || !user.value) return;
      isProcessing.value = true;
      successMessage.value = '';
      errorMessage.value = '';
      try {
        const characterActions = projectFunctions.httpsCallable('gameActions');
        const result = await characterActions({
          action: 'characterActions',
          subAction: 'manageStats',
          type: 'reset'
        });
        if (result.data.success) {
          stats.value = result.data.updatedStats;
          statPoints.value = result.data.newStatPoints;
          tempStats.value = { ...result.data.updatedStats };
          successMessage.value = result.data.message || 'รีสเตตัสสำเร็จ';
          showConfirmReset.value = false;
        } else {
          throw new Error(result.data.message || 'การรีสเตตัสล้มเหลว');
        }
      } catch (err) {
        errorMessage.value = err.message || 'เกิดข้อผิดพลาดในการรีสเตตัส';
      } finally {
        isProcessing.value = false;
      }
    };
    const resetEdits = () => { tempStats.value = { ...stats.value }; };
    const cancelModal = () => {
      resetEdits();
      showConfirmReset.value = false;
      emit('cancelModal');
    };

    return {
      // data
      stats, tempStats, statPoints, cash, level,
      isLoading, isLoadingAuth, error, user,
      isProcessing, successMessage, errorMessage,
      // ui
      activeTab, statOrder, labelMap, showConfirmReset,
      remainingPoints, usedPoints, newStatPoints, progressWidth,
      // methods
      increaseStat, decreaseStat, clampStat,
      confirmUpgrade, confirmReset, cancelModal, resetEdits, switchTab
    };
  },
};
</script>
<style scoped>
/* Animations compatible with Tailwind v2 */
@keyframes modalFade { from { opacity: 0; } to { opacity: 1; } }
.animate-modalFade { animation: modalFade 0.25s ease-out; }
@keyframes scaleIn { from { transform: scale(0.98); opacity: 0; } to { transform: scale(1); opacity: 1; } }
.animate-scaleIn { animation: scaleIn 0.25s ease-out; }
/* Spinner fallback (for some environments) */
.animate-spin { animation: spin 1s linear infinite; }
@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
/* Scrollbar */
#style-1::-webkit-scrollbar { width: 5px; }
#style-1::-webkit-scrollbar-track { background: #374151; border-radius: 10px; }
#style-1::-webkit-scrollbar-thumb { background: #4b5563; border-radius: 10px; }
#style-1::-webkit-scrollbar-thumb:hover { background: #6b7280; }
#style-1 { scrollbar-width: thin; scrollbar-color: #4b5563 #374151; }
/* Helpers */
.max-h-72 { max-height: 18rem; }
button:disabled { opacity: 0.6; cursor: not-allowed; }
button:not(:disabled):active { transform: scale(0.98); }
/* Number input arrows */
input[type="number"]::-webkit-outer-spin-button,
input[type="number"]::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
input[type="number"] { -moz-appearance: textfield; }
</style>